"use client";

import React, { useState } from "react";
import { useToast } from "./Toast";

interface ViralContentGeneratorProps {
  className?: string;
}

export default function ViralContentGenerator({ className = "" }: ViralContentGeneratorProps) {
  const [contentType, setContentType] = useState("REELS");
  const [platform, setPlatform] = useState("INSTAGRAM");
  const [topic, setTopic] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [hookType, setHookType] = useState("SHOCK_VALUE");
  const [emotion, setEmotion] = useState("CURIOSIDADE");
  const [callToAction, setCallToAction] = useState("COMPARTILHAMENTO");
  const [viralContent, setViralContent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const generateViralContent = async () => {
    if (!contentType || !platform || !topic || !targetAudience) {
      addToast("⚠️ Preencha todos os campos obrigatórios", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/viral-content", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          contentType, 
          platform, 
          topic, 
          targetAudience, 
          hookType, 
          emotion, 
          callToAction 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setViralContent(data);
        addToast("🚀 Conteúdo viral gerado com sucesso!", "success");
      } else {
        const error = await response.json();
        addToast(error.error || "Erro ao gerar conteúdo viral", "error");
      }
    } catch (error) {
      console.error("Erro:", error);
      addToast("Erro ao gerar conteúdo viral", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const copyContent = (content: string) => {
    navigator.clipboard.writeText(content);
    addToast("📋 Conteúdo copiado!", "success");
  };

  return (
    <div className={`bg-gradient-to-br from-yellow-900 via-orange-900 to-red-900 text-white rounded-lg p-6 border border-yellow-500/30 font-mono ${className}`}>
      <div className="mb-6">
        <h3 className="text-2xl font-bold mb-2 text-yellow-300">
          🚀 VIRAL CONTENT GENERATOR 🚀
        </h3>
        <div className="text-xs text-yellow-400 mb-4">
          [VIRAL_ENGINE] v2.0.0 | STATUS: ACTIVE | AUTH: VIRAL_MASTER
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs text-yellow-400 mb-2">CONTENT_TYPE:</label>
          <select
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
            className="w-full bg-yellow-950/50 border border-yellow-500/50 text-yellow-300 px-3 py-2 rounded focus:outline-none focus:border-yellow-400 focus:shadow-[0_0_10px_rgba(250,204,21,0.3)]"
          >
            <option value="REELS">REELS_TIKTOK</option>
            <option value="STORIES">STORIES</option>
            <option value="POSTS">INSTAGRAM_POSTS</option>
            <option value="VIDEOS">YOUTUBE_SHORTS</option>
            <option value="CARROSSEL">CAROUSEL_POSTS</option>
            <option value="THREADS">TWITTER_THREADS</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-yellow-400 mb-2">PLATFORM:</label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="w-full bg-yellow-950/50 border border-yellow-500/50 text-yellow-300 px-3 py-2 rounded focus:outline-none focus:border-yellow-400"
          >
            <option value="INSTAGRAM">INSTAGRAM</option>
            <option value="TIKTOK">TIKTOK</option>
            <option value="YOUTUBE">YOUTUBE</option>
            <option value="FACEBOOK">FACEBOOK</option>
            <option value="TWITTER">TWITTER</option>
            <option value="LINKEDIN">LINKEDIN</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-yellow-400 mb-2">TOPIC:</label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="w-full bg-yellow-950/50 border border-yellow-500/50 text-yellow-300 px-3 py-2 rounded focus:outline-none focus:border-yellow-400"
            placeholder="ex: Marketing Digital para Iniciantes"
          />
        </div>

        <div>
          <label className="block text-xs text-yellow-400 mb-2">TARGET_AUDIENCE:</label>
          <input
            type="text"
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            className="w-full bg-yellow-950/50 border border-yellow-500/50 text-yellow-300 px-3 py-2 rounded focus:outline-none focus:border-yellow-400"
            placeholder="ex: Jovens empreendedores 18-30 anos"
          />
        </div>

        <div>
          <label className="block text-xs text-yellow-400 mb-2">HOOK_TYPE:</label>
          <select
            value={hookType}
            onChange={(e) => setHookType(e.target.value)}
            className="w-full bg-yellow-950/50 border border-yellow-500/50 text-yellow-300 px-3 py-2 rounded focus:outline-none focus:border-yellow-400"
          >
            <option value="SHOCK_VALUE">SHOCK_VALUE</option>
            <option value="CURIOSIDADE">CURIOSIDADE</option>
            <option value="CONTROVÉRSIA">CONTROVERSY</option>
            <option value="EMOÇÃO">EMOTION</option>
            <option value="MISTÉRIO">MYSTERY</option>
            <option value="URGÊNCIA">URGENCY</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-yellow-400 mb-2">PRIMARY_EMOTION:</label>
          <select
            value={emotion}
            onChange={(e) => setEmotion(e.target.value)}
            className="w-full bg-yellow-950/50 border border-yellow-500/50 text-yellow-300 px-3 py-2 rounded focus:outline-none focus:border-yellow-400"
          >
            <option value="CURIOSIDADE">CURIOSITY</option>
            <option value="SURPRESA">SURPRISE</option>
            <option value="ALEGRIA">JOY</option>
            <option value="RAIVA">ANGER</option>
            <option value="MEDO">FEAR</option>
            <option value="INSPIRAÇÃO">INSPIRATION</option>
            <option value="NOSTALGIA">NOSTALGIA</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-yellow-400 mb-2">CALL_TO_ACTION:</label>
          <select
            value={callToAction}
            onChange={(e) => setCallToAction(e.target.value)}
            className="w-full bg-yellow-950/50 border border-yellow-500/50 text-yellow-300 px-3 py-2 rounded focus:outline-none focus:border-yellow-400"
          >
            <option value="COMPARTILHAMENTO">SHARE</option>
            <option value="COMENTÁRIO">COMMENT</option>
            <option value="SALVAR">SAVE</option>
            <option value="MARCAR">TAG</option>
            <option value="CLIQUE">CLICK</option>
            <option value="SEGUIR">FOLLOW</option>
          </select>
        </div>
      </div>

      <button
        onClick={generateViralContent}
        disabled={isLoading || !contentType || !platform || !topic || !targetAudience}
        className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 text-white py-3 px-6 rounded font-mono text-sm hover:from-yellow-700 hover:to-orange-700 disabled:from-gray-600 disabled:to-gray-600 transition-all duration-300 mb-6"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <span className="animate-pulse">⚡</span> GENERATING_VIRAL...
          </span>
        ) : (
          <span className="flex items-center justify-center">
            🚀 [CREATE_VIRAL] 🚀
          </span>
        )}
      </button>

      {viralContent && (
        <div className="mt-6 space-y-4">
          <div className="text-xs text-yellow-400 mb-4">
            [VIRAL_CONTENT_GENERATED] | SCORE: {viralContent.metadata?.viralScore} | REACH: {viralContent.metadata?.estimatedReach}
          </div>
          
          {/* Hook Section */}
          <div className="bg-yellow-950/30 border border-yellow-500/30 rounded p-4 mb-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-lg font-bold text-yellow-300">
                ⚡ VIRAL_HOOK (3 SEGUNDOS)
              </h4>
              <button
                onClick={() => copyContent(viralContent.hook?.text)}
                className="text-xs bg-yellow-500/20 px-2 py-1 rounded hover:bg-yellow-500/30"
              >
                [COPY_HOOK]
              </button>
            </div>
            
            <div className="bg-black/30 p-3 rounded">
              <div className="text-yellow-300 text-lg font-bold">{viralContent.hook?.text}</div>
              <div className="text-yellow-400 text-sm mt-2">
                <span className="text-yellow-500">TYPE:</span> {viralContent.hook?.type}
                <span className="ml-3 text-yellow-500">IMPACT:</span> {viralContent.hook?.impact_score}/10
              </div>
            </div>
          </div>

          {/* Content Structure */}
          <div className="bg-yellow-950/30 border border-yellow-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-yellow-300 mb-3">📝 CONTENT_STRUCTURE</h4>
            
            <div className="mb-3">
              <h5 className="text-sm font-bold text-yellow-400 mb-2">TITLE:</h5>
              <div className="text-yellow-300 text-lg font-bold bg-black/30 p-2 rounded">{viralContent.content?.title}</div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-yellow-400 mb-2">STORYTELLING:</h5>
              <div className="text-yellow-300 bg-black/30 p-2 rounded leading-relaxed">{viralContent.content?.body}</div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-yellow-400 mb-2">CLIMAX:</h5>
              <div className="text-yellow-300 bg-black/30 p-2 rounded font-bold">{viralContent.content?.climax}</div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-yellow-400 mb-2">RESOLUTION:</h5>
              <div className="text-yellow-300 bg-black/30 p-2 rounded">{viralContent.content?.resolution}</div>
            </div>
          </div>

          {/* Visual Elements */}
          <div className="bg-yellow-950/30 border border-yellow-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-yellow-300 mb-3">🎨 VISUAL_ELEMENTS</h4>
            
            <div className="mb-3">
              <h5 className="text-sm font-bold text-yellow-400 mb-2">THUMBNAIL_DESCRIPTION:</h5>
              <div className="text-yellow-300 bg-black/30 p-2 rounded">{viralContent.visual_elements?.thumbnail_description}</div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-yellow-400 mb-2">COLOR_SCHEME:</h5>
              <div className="text-yellow-300 bg-black/30 p-2 rounded">{viralContent.visual_elements?.color_scheme}</div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-yellow-400 mb-2">TEXT_OVERLAYS:</h5>
              <div className="flex flex-wrap gap-1">
                {viralContent.visual_elements?.text_overlays?.map((overlay: string, index: number) => (
                  <span key={index} className="bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded text-xs">
                    {overlay}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Engagement Hacks */}
          <div className="bg-yellow-950/30 border border-yellow-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-yellow-300 mb-3">🎯 ENGAGEMENT_HACKS</h4>
            <div className="space-y-2">
              {viralContent.engagement_hacks?.map((hack: string, index: number) => (
                <div key={index} className="bg-black/30 p-2 rounded">
                  <div className="text-yellow-300 text-sm">• {hack}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Psychology Triggers */}
          <div className="bg-yellow-950/30 border border-yellow-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-yellow-300 mb-3">🧠 PSYCHOLOGY_TRIGGERS</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {viralContent.psychology_triggers?.map((trigger: string, index: number) => (
                <div key={index} className="bg-black/30 p-2 rounded">
                  <div className="text-yellow-300 text-sm">🧠 {trigger}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hashtags */}
          <div className="bg-yellow-950/30 border border-yellow-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-yellow-300 mb-3"># VIRAL_HASHTAGS</h4>
            
            <div className="mb-3">
              <h5 className="text-sm font-bold text-yellow-400 mb-2">PRIMARY:</h5>
              <div className="flex flex-wrap gap-1">
                {viralContent.hashtags?.primary?.map((tag: string, index: number) => (
                  <span key={index} className="bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-yellow-400 mb-2">TRENDING:</h5>
              <div className="flex flex-wrap gap-1">
                {viralContent.hashtags?.trending?.map((tag: string, index: number) => (
                  <span key={index} className="bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <h5 className="text-sm font-bold text-yellow-400 mb-2">NICHE:</h5>
              <div className="flex flex-wrap gap-1">
                {viralContent.hashtags?.niche?.map((tag: string, index: number) => (
                  <span key={index} className="bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Variations */}
          <div className="bg-yellow-950/30 border border-yellow-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-yellow-300 mb-3">📣 CTA_VARIATIONS</h4>
            <div className="space-y-2">
              {viralContent.cta_variations?.map((cta: string, index: number) => (
                <div key={index} className="bg-black/30 p-2 rounded">
                  <div className="text-yellow-300 text-sm font-bold">{cta}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Platform Optimization */}
          <div className="bg-yellow-950/30 border border-yellow-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-yellow-300 mb-3">📱 PLATFORM_OPTIMIZATION</h4>
            <div className="space-y-2">
              {Object.entries(viralContent.platform_optimization || {}).map(([platform, optimization]: [string, any], index: number) => (
                <div key={index} className="bg-black/30 p-2 rounded">
                  <div className="text-yellow-400 text-sm font-bold">{platform.toUpperCase()}:</div>
                  <div className="text-yellow-300 text-xs">{optimization}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Viral Metrics */}
          <div className="bg-yellow-950/30 border border-yellow-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-yellow-300 mb-3">📊 VIRAL_METRICS</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="bg-black/30 p-2 rounded text-center">
                <div className="text-yellow-300 font-bold">{viralContent.viral_metrics?.predicted_views}</div>
                <div className="text-yellow-400 text-xs">Predicted Views</div>
              </div>
              <div className="bg-black/30 p-2 rounded text-center">
                <div className="text-yellow-300 font-bold">{viralContent.viral_metrics?.predicted_engagement}</div>
                <div className="text-yellow-400 text-xs">Engagement Rate</div>
              </div>
              <div className="bg-black/30 p-2 rounded text-center">
                <div className="text-yellow-300 font-bold">{viralContent.viral_metrics?.predicted_shares}</div>
                <div className="text-yellow-400 text-xs">Predicted Shares</div>
              </div>
              <div className="bg-black/30 p-2 rounded text-center">
                <div className="text-yellow-300 font-bold">{viralContent.viral_metrics?.viral_potential}</div>
                <div className="text-yellow-400 text-xs">Viral Potential</div>
              </div>
            </div>
          </div>

          {/* Posting Strategy */}
          <div className="bg-yellow-950/30 border border-yellow-500/30 rounded p-4 mb-4">
            <h4 className="text-lg font-bold text-yellow-300 mb-3">📅 POSTING_STRATEGY</h4>
            <div className="bg-black/30 p-2 rounded">
              <div className="text-yellow-300">
                <span className="text-yellow-400">Best Time:</span> {viralContent.posting_strategy?.best_time}
              </div>
              <div className="text-yellow-300">
                <span className="text-yellow-400">Frequency:</span> {viralContent.posting_strategy?.frequency}
              </div>
              <div className="text-yellow-300">
                <span className="text-yellow-400">Caption Length:</span> {viralContent.posting_strategy?.caption_length}
              </div>
              <div className="text-yellow-300">
                <span className="text-yellow-400">Emoji Strategy:</span> {viralContent.posting_strategy?.emoji_usage}
              </div>
            </div>
          </div>

          <div className="text-xs text-yellow-400 mt-4">
            [VIRAL_CONTENT_COMPLETE] | WORD_COUNT: {viralContent.content?.word_count} | PLATFORM: {platform} | AUTH: ADMIN
          </div>
        </div>
      )}
    </div>
  );
}
